// =====================================================
// COMPOSANT: CommercialsTable — tableau commerciaux dépliable (RUN 3)
// =====================================================
// Colonnes : Commercial assigné | BPI traités | ID obtenus | Taux. Clic sur une
// ligne → sous-stats (Conversion BPI→PI, PI→ID, Délai moyen, Appels/ID). Ligne
// « Non assigné » incluse. Réel ≈ 25 lignes → tableau REPLIABLE (tout le corps)
// + SCROLL interne. Design system crm-4l (zinc). Aucune couleur navy/ochre.

import { useState } from 'react';
import { ChevronDown, ChevronRight, Users } from 'lucide-react';
import type { CommercialRow } from '@/lib/dashboard-stats';
import { fmtNumber, fmtRate } from './rate-format';

interface CommercialsTableProps {
  rows: CommercialRow[];
}

/** Une paire label / valeur des sous-stats dépliées. */
function SubStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-lg border border-zinc-200 bg-white px-3 py-2">
      <span className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">{label}</span>
      <span className="text-base font-semibold tabular-nums text-zinc-900">{value}</span>
    </div>
  );
}

function CommercialRowItem({ row }: { row: CommercialRow }) {
  const [open, setOpen] = useState(false);
  const isUnassigned = row.userId == null;

  return (
    <>
      <tr
        className="cursor-pointer border-t border-zinc-100 transition-colors hover:bg-zinc-50"
        onClick={() => setOpen((o) => !o)}
      >
        <td className="py-2.5 pl-3 pr-2">
          <div className="flex items-center gap-2">
            {open ? (
              <ChevronDown className="h-4 w-4 shrink-0 text-zinc-400" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-zinc-400" />
            )}
            <span
              className={[
                'text-sm font-medium',
                isUnassigned ? 'italic text-zinc-500' : 'text-zinc-900',
              ].join(' ')}
            >
              {row.name}
            </span>
          </div>
        </td>
        <td className="py-2.5 pr-3 text-right text-sm tabular-nums text-zinc-700">
          {row.bpiTraites.toLocaleString('fr-FR')}
        </td>
        <td className="py-2.5 pr-3 text-right text-sm tabular-nums text-zinc-700">
          {row.idObtenus.toLocaleString('fr-FR')}
        </td>
        <td className="py-2.5 pr-3 text-right text-sm font-semibold tabular-nums text-zinc-900">
          {fmtRate(row.taux.value)}
        </td>
      </tr>
      {open ? (
        <tr className="border-t border-zinc-100 bg-zinc-50/60">
          <td colSpan={4} className="px-3 py-3">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <SubStat label="Conversion BPI→PI" value={fmtRate(row.sub.bpiToPi.value)} />
              <SubStat label="Conversion PI→ID" value={fmtRate(row.sub.piToId.value)} />
              <SubStat
                label="Délai moyen"
                value={
                  row.sub.delaiMoyenJours == null ? '—' : fmtNumber(row.sub.delaiMoyenJours, ' j')
                }
              />
              <SubStat label="Appels / ID" value={fmtNumber(row.sub.appelsParId)} />
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

export function CommercialsTable({ rows }: CommercialsTableProps) {
  // Repliable : le corps du tableau se plie/déplie en entier (≈25 lignes réelles).
  const [collapsed, setCollapsed] = useState(false);
  const activeCount = rows.filter((r) => r.userId != null).length;

  return (
    <div className="mt-4 overflow-hidden rounded-xl border border-zinc-200">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
        className="flex w-full items-center justify-between gap-2 bg-zinc-50 px-3 py-2 text-left transition-colors hover:bg-zinc-100"
      >
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-zinc-600">
          <Users className="h-3.5 w-3.5" />
          Détail par commercial
          <span className="rounded-full bg-zinc-200 px-1.5 py-0.5 text-[10px] font-medium text-zinc-600">
            {activeCount} assignés
          </span>
        </span>
        {collapsed ? (
          <ChevronRight className="h-4 w-4 text-zinc-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-zinc-400" />
        )}
      </button>

      {!collapsed ? (
        rows.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-zinc-400">
            Aucune activité commerciale sur la période.
          </div>
        ) : (
          // Scroll interne : garde le tableau compact malgré ~25 lignes.
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="text-[11px] uppercase tracking-wide text-zinc-500">
                  <th className="py-2 pl-3 pr-2 text-left font-semibold">Commercial assigné</th>
                  <th className="py-2 pr-3 text-right font-semibold">BPI traités</th>
                  <th className="py-2 pr-3 text-right font-semibold">ID obtenus</th>
                  <th className="py-2 pr-3 text-right font-semibold">Taux</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <CommercialRowItem key={row.userId ?? '__unassigned__'} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </div>
  );
}
